import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Runs the WebAuthn creation ceremony, then hands the result to the form
// as an ordinary hidden field. The submit that follows is a plain Rails
// POST, so redirects, flash and re-rendered errors all behave normally.
export default class extends Controller {
  static targets = ["credential", "failure", "failureMessage", "location",
    "phoneOption", "phoneRadio", "phoneText", "phoneUnsupported"]
  static values = { challengeUrl: String, unsupported: String, cancelled: String }

  async connect() {
    if (await this.#phoneUnreachable()) this.#ruleOutPhone()
  }

  async enrol(event) {
    if (this.credentialTarget.value) return

    event.preventDefault()

    if (!window.PublicKeyCredential) {
      this.#fail(this.unsupportedValue)
      return
    }

    try {
      const options = await this.#options()
      const credential = await navigator.credentials.create({ publicKey: options })

      this.credentialTarget.value = JSON.stringify(this.#serialise(credential))
      this.element.requestSubmit()
    } catch {
      this.#fail(this.cancelledValue)
    }
  }

  async #options() {
    const response = await post(this.challengeUrlValue, { responseKind: "json" })
    const options = await response.json

    options.challenge = this.#decode(options.challenge)
    options.user.id = this.#decode(options.user.id)
    options.excludeCredentials = (options.excludeCredentials || []).map(
      (credential) => ({ ...credential, id: this.#decode(credential.id) })
    )

    const chosen = this.locationTargets.find((location) => location.checked)

    if (chosen) {
      options.hints = [chosen.value]

      // A hint only reorders the browser's picker, and Chrome still
      // leads with the provider built into the machine. Choosing the
      // phone means "not this machine", so that one is ruled out rather
      // than merely discouraged.
      //
      // The reverse is not enforced: restricting to the platform
      // authenticator fails outright on a machine that has none, where
      // falling back to the picker still gets someone a passkey.
      if (chosen.value === "hybrid") {
        options.authenticatorSelection = {
          ...options.authenticatorSelection,
          authenticatorAttachment: "cross-platform"
        }
      }
    }

    return options
  }

  #serialise(credential) {
    return {
      type: credential.type,
      id: credential.id,
      rawId: this.#encode(credential.rawId),
      authenticatorAttachment: credential.authenticatorAttachment,
      clientExtensionResults: credential.getClientExtensionResults(),
      response: {
        attestationObject: this.#encode(credential.response.attestationObject),
        clientDataJSON: this.#encode(credential.response.clientDataJSON)
      }
    }
  }

  // Only a browser that answers the question can rule the phone out.
  // One that has never heard of it — Firefox among them — is left
  // offering the choice rather than told it cannot have it.
  async #phoneUnreachable() {
    if (!window.PublicKeyCredential?.getClientCapabilities) return false

    try {
      const capabilities = await PublicKeyCredential.getClientCapabilities()

      return capabilities.hybridTransport === false
    } catch {
      return false
    }
  }

  #ruleOutPhone() {
    this.phoneRadioTarget.disabled = true
    this.phoneRadioTarget.checked = false
    this.phoneOptionTarget.classList.add("cursor-not-allowed")
    this.phoneTextTarget.classList.add("opacity-50")
    this.phoneUnsupportedTarget.classList.remove("hidden")
  }

  #fail(message) {
    this.failureMessageTarget.textContent = message
    this.failureTarget.classList.remove("hidden")
  }

  // WebAuthn speaks base64url without padding; atob needs standard
  // base64 with it.
  #decode(value) {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")

    return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0))
  }

  #encode(buffer) {
    const binary = String.fromCharCode(...new Uint8Array(buffer))

    return btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "")
  }
}
